import styled from '@emotion/styled';
import type { Theme } from '@trinity/chat-components/types/theme';
import { getThemeValue, getThemeColor, THEME } from './DynamicForm.styles.theme';

/**
 * Type definitions for styled component props
 */
type StyledComponentProps = {
  theme?: Theme.Theme;
};

// =============================================================================
// SUBMITTED/DRAFT VIEW COMPONENTS
// =============================================================================

export const SubmittedSectionBox = styled.div<StyledComponentProps>(({ theme }) => ({
  border: `1px solid ${getThemeColor.border(theme)}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.lg,
  marginBottom: THEME.spacing.lg,
  backgroundColor: getThemeColor.background(theme),
  ':last-of-type': {
    marginBottom: 0,
  },
}));

export const SubmittedSectionContainer = styled.div<StyledComponentProps>(({ theme }) => ({
  marginBottom: 0,
  paddingBottom: 0,
}));

export const SubmittedSectionTitle = styled.h3<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.lg,
  fontWeight: 600,
  color: getThemeColor.text(theme),
  margin: 0,
  marginBottom: THEME.spacing.lg,
  letterSpacing: '0.02em',
}));

export const SubmittedWrapper = styled.div<StyledComponentProps>(({ theme }) => ({
  width: '100%',
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${getThemeColor.borderLight(theme)}`,
  backgroundColor: getThemeColor.background(theme),
}));

export const SubmittedHeader = styled('button', {
  shouldForwardProp: prop => prop !== 'expanded',
})<StyledComponentProps & { expanded?: boolean }>(({ expanded, theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: getThemeColor.backgroundLight(theme),
  border: 'none',
  padding: `${THEME.spacing.lg} ${THEME.spacing.xl}`,
  cursor: 'pointer',
  textAlign: 'left',
  borderRadius: expanded
    ? `${THEME.borderRadius.md} ${THEME.borderRadius.md} 0 0`
    : THEME.borderRadius.md,
  color: getThemeColor.text(theme),
}));

export const SubmittedHeaderContent = styled.div<StyledComponentProps>({
  display: 'flex',
  flexDirection: 'column',
  gap: THEME.spacing.xs,
});

export const SubmittedTitle = styled.span<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.lg,
  fontWeight: 600,
  color: getThemeColor.text(theme),
}));

export const SubmittedMeta = styled.span<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.sm,
  color: getThemeColor.text(theme),
  opacity: 0.75,
}));

export const SubmittedToggleIcon = styled('div', {
  shouldForwardProp: prop => prop !== 'expanded',
})<StyledComponentProps & { expanded: boolean }>(({ expanded, theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s ease',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  color: getThemeColor.text(theme),
}));

export const SubmittedContent = styled.div<StyledComponentProps>(({ theme }) => ({
  borderTop: `1px solid ${getThemeColor.border(theme)}`,
  padding: `${THEME.spacing.xl}`,
  borderRadius: `0 0 ${THEME.borderRadius.md} ${THEME.borderRadius.md}`,
  backgroundColor: getThemeColor.background(theme),
}));

export const SubmittedFieldGrid = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginBottom: 0,
}));

export const SubmittedFieldRow = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.md,
  ':last-of-type': {
    marginBottom: 0,
  },
}));

export const SubmittedFieldLabel = styled.div<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.xs,
  fontWeight: 600,
  color: getThemeColor.textSecondary(theme),
  marginBottom: THEME.spacing.xs,
  letterSpacing: '0.01em',
  '& .required-asterisk': {
    color: getThemeColor.error(theme),
    fontWeight: 'normal',
    marginLeft: THEME.spacing.xs,
  },
}));

export const SubmittedFieldValue = styled.div<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.sm,
  color: getThemeColor.text(theme),
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.6,
  fontWeight: 400,
  width: '100%',
}));

export const SubmittedEmptyState = styled.div<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.sm,
  color: getThemeColor.textSecondary(theme),
}));

export const RichContentViewport = styled.div({
  width: '100%',
  overflow: 'hidden',
  display: 'block',
  '& iframe': {
    width: '100%',
    border: 'none',
  },
  '& .css-d7ynk3-SecureHtmlRenderer__mixedContentContainerStyles': {
    width: '100%',
  },
});

